import {
	Bell,
	Calendar,
	CalendarDays,
	ChevronDown,
	Home,
	type LucideIcon,
	MessageCircle,
	MoreVertical,
	Plus,
	Search,
	SquarePlay,
	Users,
	Wifi,
} from "lucide-react";
import type { ReactNode } from "react";
import { CommunityLogo } from "@/src/common/components/app-mock/community-logo";
import { MOCK_COMMUNITY, type MockCommunity } from "@/src/common/components/app-mock/mock-community";
import { UnreadBadge } from "@/src/common/components/app-mock/primitives";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

type MobileTabId = "home" | "members" | "video" | "meetings" | "events" | "more";

interface MobileTab {
	id: MobileTabId;
	label: string;
	icon: LucideIcon;
}

const MOBILE_TABS: ReadonlyArray<MobileTab> = [
	{ id: "home", label: "Home", icon: Home },
	{ id: "members", label: "Members", icon: Users },
	{ id: "video", label: "Video", icon: SquarePlay },
	{ id: "meetings", label: "Meetings", icon: Calendar },
	{ id: "events", label: "Events", icon: CalendarDays },
	{ id: "more", label: "More", icon: MoreVertical },
];

const MOBILE_COLORS = {
	text: "#1A1A1A",
	textSecondary: "#6B7280",
	border: "#E5E7EB",
	background: "#FFFFFF",
	videoBg: "#000000",
	error: "#EF4444",
	success: "#22C55E",
	warning: "#F59E0B",
};

export const MOBILE_TOKENS = MOBILE_COLORS;

function StatusBar({ light }: { light: boolean }) {
	const color = light ? "#fff" : MOBILE_COLORS.text;
	return (
		<div className="flex h-9 shrink-0 items-end justify-between px-5 pb-1.5" aria-hidden="true" style={{ color }} data-mock="status-bar">
			<span className="text-[11px] font-semibold tabular-nums">9:41</span>
			<span className="flex items-center gap-1.5">
				<span className="flex items-end gap-px">
					<span className="h-1 w-0.5 rounded-sm" style={{ background: color }} />
					<span className="h-1.5 w-0.5 rounded-sm" style={{ background: color }} />
					<span className="h-2 w-0.5 rounded-sm" style={{ background: color }} />
					<span className="h-2.5 w-0.5 rounded-sm" style={{ background: color }} />
				</span>
				<Wifi className="size-3" />
				<span className="relative flex h-2.5 w-5 items-center rounded-[3px] border p-px" style={{ borderColor: color }}>
					<span className="h-full w-full rounded-[1.5px]" style={{ background: color }} />
					<span className="absolute -right-[3px] top-1/2 h-1 w-[2px] -translate-y-1/2 rounded-r-sm" style={{ background: color }} />
				</span>
			</span>
		</div>
	);
}

interface CommunityHeaderProps {
	community: MockCommunity;
}

function CommunityHeader({ community }: CommunityHeaderProps) {
	return (
		<div
			className="flex shrink-0 items-center justify-between border-b px-3 py-2"
			style={{ borderColor: MOBILE_COLORS.border, background: MOBILE_COLORS.background }}
			aria-hidden="true"
			data-mock="community-header"
		>
			<span className="flex min-w-0 items-center gap-2">
				<CommunityLogo size={30} rounded="rounded-lg" />
				<span className="truncate text-[13px] font-bold" style={{ color: MOBILE_COLORS.text }}>
					{community.name}
				</span>
				<ChevronDown className="size-3.5 shrink-0" style={{ color: MOBILE_COLORS.textSecondary }} />
			</span>
			<span className="flex shrink-0 items-center gap-3.5" style={{ color: MOBILE_COLORS.text }}>
				<Search className="size-[18px]" />
				<span className="relative inline-flex">
					<Bell className="size-[18px]" />
					<UnreadBadge count={3} floating />
				</span>
				<span className="relative inline-flex">
					<MessageCircle className="size-[18px]" />
					<UnreadBadge count={2} floating />
				</span>
			</span>
		</div>
	);
}

function TabBar({ activeTab }: { activeTab: MobileTabId }) {
	const { palette } = useCommunityVariant();
	return (
		<nav
			aria-hidden="true"
			className="flex shrink-0 items-start justify-around border-t px-1 pt-1.5"
			style={{ borderColor: MOBILE_COLORS.border, background: MOBILE_COLORS.background }}
			data-mock="tab-bar"
		>
			{MOBILE_TABS.map((tab) => {
				const Icon = tab.icon;
				const isActive = tab.id === activeTab;
				return (
					<span
						key={tab.id}
						className="flex w-10 flex-col items-center gap-0.5"
						style={{ color: isActive ? palette.accent : MOBILE_COLORS.textSecondary }}
						data-mock="tab-item"
					>
						<Icon className="size-5" />
						<span className="text-[9px] font-medium leading-3" style={isActive ? { color: palette.accentInk } : undefined}>
							{tab.label}
						</span>
					</span>
				);
			})}
		</nav>
	);
}

function MobileFab() {
	const { palette } = useCommunityVariant();
	return (
		<span
			aria-hidden="true"
			className="absolute bottom-4 right-4 flex size-11 items-center justify-center rounded-full text-white shadow-lg"
			style={{ background: palette.accent }}
			data-mock="fab"
		>
			<Plus className="size-5" />
		</span>
	);
}

export interface MobileAppShellProps {
	activeTab: MobileTabId;
	children: ReactNode;
	community?: MockCommunity;
	hideHeader?: boolean;
	fab?: boolean;
	dark?: boolean;
	className?: string;
}

export function MobileAppShell({
	activeTab,
	children,
	community = MOCK_COMMUNITY,
	hideHeader,
	fab,
	dark,
	className = "",
}: MobileAppShellProps) {
	const background = dark ? MOBILE_COLORS.videoBg : MOBILE_COLORS.background;
	return (
		<div
			className={`flex h-full w-full flex-col overflow-hidden font-sans ${className}`}
			style={{ background, color: MOBILE_COLORS.text }}
			data-mock="mobile-shell"
		>
			<StatusBar light={Boolean(dark)} />
			{hideHeader ? null : <CommunityHeader community={community} />}
			<div className="relative min-h-0 min-w-0 flex-1 overflow-hidden" data-mock="screen">
				{children}
				{fab ? <MobileFab /> : null}
			</div>
			<TabBar activeTab={activeTab} />
			<div className="flex h-4 shrink-0 items-center justify-center" style={{ background: MOBILE_COLORS.background }} aria-hidden="true">
				<span className="h-1 w-24 rounded-full bg-black/80" />
			</div>
		</div>
	);
}
